import React, { useEffect, useRef, useState } from "react";
import { supabase } from "./supabaseClient"; // adjust import if using another auth

export default function TermsModal() {
  const dialogRef = useRef(null);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [isOpen, setIsOpen] = useState(null); // null = loading

  // Inside TermsModal component:

  // Disable scrolling ONLY while the modal is actually shown. Previously
  // this fired on mount unconditionally, which broke scrolling on the
  // /terms page (where the modal renders null but the effect still ran).
  useEffect(() => {
    if (!isOpen) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  // Focus first element on mount
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const titleEl = dialog.querySelector("#terms-title");
    titleEl && titleEl.focus();
  }, []);

  useEffect(() => {
    // Don't render the modal on the legal doc pages themselves — the modal
    // links to /terms (and later /privacy, /research-consent); showing it
    // there would block users from reading what they're being asked to accept.
    const LEGAL_ROUTES = ['/terms', '/privacy', '/research-consent'];
    if (LEGAL_ROUTES.includes(window.location.pathname)) {
      setIsOpen(false);
      return;
    }

    // Dev/preview escape hatch: force the modal open regardless of acceptance
    if (new URLSearchParams(window.location.search).has('showTerms')) {
      setIsOpen(true);
      return;
    }

    const checkTerms = async () => {
      try {

        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        if (!token) {
          console.error("No valid session token found");
          return;
        }

        const res = await fetch(`${backendUrl}/check-terms`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });

        const data = await res.json();
        setIsOpen(!data.accepted); // show modal if not accepted
      } catch (err) {
        console.error(err);
        setIsOpen(true); // fallback: show modal
      }
    };
    checkTerms();
  }, [backendUrl]);


  const handleAccept = async () => {
    if (!checked) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        console.error("No valid session token found");
        return;
      }

      const res = await fetch(`${backendUrl}/accept-terms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to accept terms");

      setIsOpen(false); // immediately hide modal after backend update
      document.body.style.overflow = ""; // re-enable scrolling
    } catch (err) {
      console.error(err);
      alert("Unable to save acceptance. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  if (isOpen === null) return null; // wait for backend check
  if (!isOpen) return null; // already accepted


  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-title"
      aria-describedby="terms-desc"
    >
      <div
        ref={dialogRef}
        className="modal-panel max-w-lg p-7 outline-none"
      >
        <h2
          id="terms-title"
          tabIndex={-1}
          className="font-display text-xl font-semibold text-ink"
        >
          Terms &amp; Conditions
        </h2>

        <div id="terms-desc" className="mt-4 space-y-3 text-sm leading-6 text-ink-muted">
          <p>
            By proceeding, you agree to our Terms &amp; Conditions. In short:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>The course history data we collect is stored anonymously and used solely to build your personalized course history and provide better course recommendations.</li>
            <li>We do not sell, share, or monetize this data in any way.</li>
            <li>Your information will never be used for advertising or shared with third parties.</li>
            <li>Our goal is to help you discover relevant courses while keeping your academic data private and secure.</li>
          </ul>
          <p className="pt-1">
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary underline underline-offset-2 hover:text-primary-dark"
            >
              Read the full Terms of Service&nbsp;↗
            </a>
          </p>
        </div>

        <label className="mt-6 flex cursor-pointer select-none items-start gap-3 rounded-xl border border-line p-4 transition-colors hover:bg-surface-alt">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-line text-primary focus:ring-primary"
          />
          <span className="text-sm text-ink">
            I have read and agree to the <span className="font-medium">Terms &amp; Conditions</span>.
          </span>
        </label>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={handleAccept}
            disabled={!checked || loading}
            className="btn-primary"
            aria-disabled={!checked || loading}
          >
            {loading ? "Saving…" : "Accept & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
